import { api } from 'careand-redux/api/ApiThunks';
import z from 'zod';
import {
  ActionResult,
  actionsActions,
  ActionType,
  ClinicalAction,
  ExecutorState,
} from 'careand-redux/slices/actions';
import { store } from 'careand-redux/store';
import { UnwrappedAppDispatch } from 'careand-redux/useAppDispatch';
import AIUtils from './AIUtils';
import CPPUtils from './CPPUtils';
import moment from 'moment-timezone';
import _ from 'lodash';
import type { Patient, Appointment, PrescriptionMedication } from '../generated/api';
import PatientUtils from './PatientUtils';
import { runSavedPromptTyped } from '../thunks/runSavedPromptTyped';

type RunningState = ExecutorState & { patient: Patient; appointment: Appointment };

type ExecutorFunction<T extends ActionType> = (
  action: ClinicalAction<T>,
  state: RunningState,
) => Promise<ActionResult[T] | void>;

const SuggestedPrefilledTemplatesSchema = z.object({
  prefilled: z.array(z.string()),
  unfilled: z.array(z.string()),
});

export class ActionExecutor {
  private dispatch: UnwrappedAppDispatch;

  constructor(dispatch: UnwrappedAppDispatch) {
    this.dispatch = dispatch;
  }

  private getState(appointmentId: string): RunningState {
    const state = store.getState();
    const executorState = state.actions.executorStateByAppointmentId[appointmentId];
    const appointment = _(state.appointments.byId)
      .values()
      .flatMap((it) => it ?? [])
      .find((it) => it.id === appointmentId);
    const patient = state.patients.byId[appointment?.patient_id ?? ''];

    if (!appointment || !patient) {
      throw new Error('Missing appointment or patient');
    }

    return { ...executorState, appointment, patient };
  }

  async execute(action: ClinicalAction<any>): Promise<void> {
    // Mark as in-progress
    action = { ...action, status: 'in-progress', updated_at: moment().unix() };
    this.dispatch(actionsActions.updateAction(action));

    try {
      let executionData: any;
      const runningState = this.getState(action.appointment_id);

      switch (action.type) {
        case 'prescription':
          executionData = await this.executePrescription(action, runningState);
          break;
        case 'requisition':
        case 'other_document':
          executionData = await this.executeDocumentSearchAndFill(action, runningState);
          break;
        case 'referral':
          executionData = await this.suggestReferrals(action, runningState);
          break;
        case 'other':
          executionData = { note: action.description };
          break;
      }

      // Mark as pending approval with execution data
      this.dispatch(
        actionsActions.updateAction({
          id: action.id,
          status: 'pending-approval',
          execution_data: executionData,
          updated_at: Date.now(),
        }),
      );
    } catch (error: any) {
      // Mark as error
      console.log(error);
      this.dispatch(
        actionsActions.updateAction({
          ...action,
          status: 'error',
          error_message: error.message,
          updated_at: Date.now(),
        }),
      );
    }
  }

  private suggestReferrals: ExecutorFunction<'referral'> = async (action, runningState) => {
    const patientId = runningState.patient.id;

    if (!patientId) {
      return { templates: [], contacts: [] };
    }

    const query = _([action.referral_name, action.specialty]).compact().value().join(' ');

    const [contacts, templates] = await Promise.all([
      this.dispatch(
        api.Contacts.getPhysicianContacts({
          name: query,
          limit: 3,
          nearPatient: patientId,
        }),
      ),
      this.dispatch(
        api.Templates.searchDocumentTemplates({
          stringResource: { value: action.query },
        }),
      ),
    ]);

    console.log('REFERRAL_SUGGESTIONS', templates, contacts);

    return {
      templates: _(templates)
        .map((it) => ({ id: it.id!, description: it.description! }))
        .take(5)
        .value(),
      contacts: _(contacts)
        .map((contact) => ({ contact }))
        .value(),
    };
  };

  private executeDocumentSearchAndFill: ExecutorFunction<
    'referral' | 'requisition' | 'other_document'
  > = async (action, runningState) => {
    const { appointment, note, transcription } = runningState;

    const state = store.getState();
    const prefilled = state.templates.templatesPrefilled;

    if (!appointment?.patient_id) {
      throw new Error('Missing appointment or patient_id');
    }

    const patientId = appointment.patient_id;

    // Get transcription
    let transcriptionStr = '';
    if (action.appointment_id) {
      const res = transcription;
      const text = res?.messages?.map((msg) => `${msg.from}: ${msg.text}`)?.join('\n');
      if (text && text.length > 0) {
        transcriptionStr = text;
      }
    }

    // Step 1: Update status - searching for template
    this.dispatch(
      actionsActions.updateAction({
        id: action.id,
        executor_message: 'Searching document templates...',
        updated_at: Date.now(),
      }),
    );

    // Step 2: Get template suggestions based on query
    const query = action.query || '';

    const templateSuggestions = await this.dispatch(
      api.Templates.searchDocumentTemplates({
        stringResource: { value: query },
      }),
    );

    if (!templateSuggestions?.length) {
      throw new Error('No suitable document templates found');
    }

    // Step 4: Get prefilled templates that match

    let suggestions: ActionResult['requisition'] | undefined;

    const demographics = `Age: ${PatientUtils.getAge(
      runningState.patient,
    )}\nGender: ${PatientUtils.getGender(runningState.patient)}`;

    if (action.type === 'requisition' || action.type === 'other_document') {
      const fields = {
        demographics: demographics,
        note: note ?? 'No note',
        transcription: transcriptionStr,
        prefilledTemplates: JSON.stringify(
          prefilled.map((prefilled) => ({
            id: prefilled.id,
            bundle: prefilled.bundle,
            title: prefilled.title,
          })),
          null,
          2,
        ),
        unfilledTemplates: JSON.stringify(
          templateSuggestions.map((it) =>
            _.pick(it, ['id', 'description', 'secondary_name', 'long_description']),
          ),
          null,
          2,
        ),
        npPrompt: query,
      };

      let result = await runSavedPromptTyped('Suggest Prefilled Document Templates', fields, {
        patientId,
      });

      result = SuggestedPrefilledTemplatesSchema.parse(result);

      suggestions = {
        templates: result.unfilled.map((id) => ({
          id,
          description: templateSuggestions.find((it) => it.id === id)?.description,
        })),
        prefilledTemplates: result.prefilled.map((id) => prefilled.find((it) => it.id === id)!),
      };

      console.log('Document Suggestions', suggestions);

      if (result) {
        this.dispatch(
          actionsActions.updateAction({
            id: action.id,
            execution_data: suggestions,
            updated_at: moment().unix(),
          }),
        );
      }
    }

    const templateToFill = suggestions?.templates[0];

    if (templateToFill) {
      this.dispatch(
        actionsActions.updateAction({
          id: action.id,
          executor_message: `Auto-filling ${templateToFill.description || 'template'}...`,
          updated_at: moment().unix(),
        }),
      );

      // Step 5: Fill document template
      const filledFields =
        (await this.dispatch(
          api.Templates.fillDocumentTemplate({
            templateId: templateToFill.id,
            fillDocumentTemplateParams: {
              appointment,
              patient_id: patientId,
              appointment_note: note ?? '',
              extra_instructions: action.description,
            },
          }),
        )) ?? [];

      for (let field of filledFields ?? []) {
        field.value = field.value === 'true' ? 'on' : field.value === 'false' ? 'off' : field.value;
      }
      console.log('FILL', JSON.stringify(filledFields, null, 2));

      this.dispatch(
        actionsActions.updateTemplateFields({
          action_id: action.id,
          template_id: templateToFill.id,
          fields: filledFields,
        }),
      );
    }
  };

  private executePrescription: ExecutorFunction<'prescription'> = async (action, runningState) => {
    const state = store.getState();
    const patientData = state.healthRecords.byId[runningState.patient.id!];
    const prescriptions = patientData?.prescriptions;
    const favoriteMedications = state.drugs.favoriteMedications;
    const allergies =
      patientData?.healthRecords?.allergies
        ?.filter((allergy) => CPPUtils.isHealthRecordItemEnabled(allergy))
        ?.map((allergy) => allergy.value.name)
        ?.join(', ') || 'NKA';

    // Get transcription
    let transcription = '';
    if (action.appointment_id) {
      const res = runningState.transcription;
      const text = res?.messages?.map((msg) => `${msg.from}: ${msg.text}`)?.join('\n');
      if (text && text.length > 0) {
        transcription = text;
      }
    }

    // Format recent prescriptions
    const prettyPrescriptions = JSON.stringify(
      _.orderBy(prescriptions ?? [], ['date'], ['desc'])
        .slice(0, 15)
        .map((it) => ({
          ...it,
          date: moment.unix(it.date).fromNow(),
        })),
      null,
      2,
    );

    const fields = {
      note: runningState.note ?? 'no note',
      appointment: JSON.stringify(runningState.appointment, null, 2),
      transcription: transcription,
      prescriptions: prettyPrescriptions,
      allergies: allergies,
      favoriteMedications: JSON.stringify(
        _.compact(favoriteMedications.map((it) => it.value)),
        null,
        2,
      ),
      additionalInstructions: `Prescribe only the medications in the following prompt: "${action.description}"`,
    };

    // Call the Generate Prescription prompt (same as useGeneratePrescription)
    const result = await runSavedPromptTyped('Generate Prescription', fields, {
      patientId: runningState.patient.id!,
    });

    // Parse medication from streaming response (tool_use blocks)
    let medication: PrescriptionMedication | undefined = undefined;
    if (result) {
      AIUtils.generateJSONSections(result).forEach((tool: any) => {
        if (tool?.type === 'tool_use' && tool.name === 'add_medication') {
          medication = tool.input.medication as PrescriptionMedication;
        }
      });
    }

    console.log('MEDICATIONS', JSON.stringify(medication, null, 2));

    return { medication };
  };
}
